// src/components/InstitutionLogin.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { EnvelopeIcon, LockClosedIcon, ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Footer from './Footer'; // Add Footer import

function InstitutionLogin({ institutionName, onLoginOrchestration, loginError, clearLoginError, initialEmail, onEmailChange }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const emailInputRef = useRef(null);
  const forgotEmailInputRef = useRef(null);
  
  // Helper function to translate error messages
  const getErrorMessage = (error) => {
    if (!error) return '';
    
    // Check if it's an error object with errorCode
    if (typeof error === 'object' && error.errorCode) {
      const errorMap = {
        'INVALID_CREDENTIALS_C2': t('auth.errors.invalidCredentials'),
        'PASSWORD_NOT_SET_C2': t('auth.errors.passwordNotSet'),
        'NOT_INSTITUTION_MEMBER_C2': t('auth.errors.notInstitutionMember'),
        'INSTITUTION_NOT_ACTIVE_C2': t('auth.errors.institutionNotActive'),
        'SUBSCRIPTION_EXPIRED_C2': t('auth.errors.subscriptionExpired'),
        'USER_POSITION_EXCEEDS_LIMIT_C2': error.message || t('auth.errors.userLimitExceeded'),
        'NO_TOKEN': t('auth.errors.sessionExpired'),
        'TOKEN_EXPIRED': t('auth.errors.sessionExpired'),
        'INVALID_TOKEN': t('auth.errors.invalidSession'),
        'VALIDATION_ERROR': t('auth.errors.validationError'),
      };
      
      return errorMap[error.errorCode] || error.message || t('auth.errors.genericError');
    }
    
    // Check if it's a string message
    if (typeof error === 'string') {
      // Common untranslated messages mapping
      const messageMap = {
        'Please correct the following errors': t('auth.errors.validationError'),
        'Invalid credentials.': t('auth.errors.invalidCredentials'),
        'Invalid credentials or account not found.': t('auth.errors.invalidCredentials'),
        'Invalid email or password': t('auth.errors.invalidCredentials'),
        'Password not set for this account. Please complete your account setup or use password reset if applicable.': t('auth.errors.passwordNotSet'),
        'You are not a member of this institution.': t('auth.errors.notInstitutionMember'),
        'This institution is not currently active.': t('auth.errors.institutionNotActive'),
        'Your institution subscription has expired. Please contact your administrator.': t('auth.errors.subscriptionExpired'),
      };
      
      // Check for exact matches first
      if (messageMap[error]) {
        return messageMap[error];
      }
      
      // Check for partial matches
      if (error.includes('Invalid credentials')) {
        return t('auth.errors.invalidCredentials');
      }
      if (error.includes('subscription')) {
        return t('auth.errors.subscriptionIssue');
      }
      if (error.includes('user limit') || error.includes('plan allows')) {
        return error; // Return as-is for detailed limit messages
      }
      
      return error; // Return as-is if not recognized
    }
    
    return t('auth.errors.genericError');
  };
  
  // Set language from institution on mount
  useEffect(() => {
    const institutionData = localStorage.getItem('selectedInstitution');
    if (institutionData) {
      try {
        const institution = JSON.parse(institutionData);
        const publicLanguage = institution.public_portal_language || 'en';
        console.log('[Institution Login] Setting language:', publicLanguage);
        i18n.changeLanguage(publicLanguage).catch(error => {
          console.error('[Institution Login] Failed to set language, falling back to English:', error);
          i18n.changeLanguage('en');
        });
      } catch (error) {
        console.error('[Institution Login] Failed to parse institution data:', error);
        i18n.changeLanguage('en');
      }
    }
  }, [i18n]);
  
  // Only update email from parent on mount, not on every change
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    } else if (initialEmail) {
      setEmail(initialEmail);
    }
    
    // Focus email field if it's empty
    setTimeout(() => {
      if (emailInputRef.current && !savedEmail && !initialEmail) {
        emailInputRef.current.focus();
      }
    }, 100);
  }, []);

  // ONLY clear password on actual new errors
  useEffect(() => {
    if (loginError) {
      setPassword('');
      setIsLoading(false);
    }
  }, [loginError]);

  // Focus forgot password email input when shown
  useEffect(() => {
    if (showForgotPassword && forgotEmailInputRef.current) {
      setTimeout(() => {
        forgotEmailInputRef.current?.focus();
      }, 100);
    }
  }, [showForgotPassword]);

  // Clear validation error when user types
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (validationError) setValidationError('');
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (validationError) setValidationError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) return;
    
    // Clear any existing errors
    setValidationError('');
    if (clearLoginError) {
      clearLoginError();
    }
    
    // CLIENT-SIDE VALIDATION
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedEmail) {
      setValidationError(t('auth.errors.emailRequired'));
      emailInputRef.current?.focus();
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setValidationError(t('auth.errors.invalidEmail'));
      emailInputRef.current?.focus();
      return;
    }
    
    if (!trimmedPassword) {
      setValidationError(t('auth.errors.passwordRequired'));
      return;
    }
    
    // Save or remove email based on remember me checkbox
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    
    // Sync with parent only when submitting
    if (onEmailChange) {
      onEmailChange(email);
    }
    
    setIsLoading(true);
    
    try {
      const result = await onLoginOrchestration(trimmedEmail, trimmedPassword);
      
      // ONLY clear on failure, keep everything on success
      if (!result || !result.success) {
        setPassword('');
        setIsLoading(false);
      }
      // On success, component will be unmounted by navigation
      
    } catch (error) {
      console.error('Login failed:', error);
      setPassword('');
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowForgotPassword(true);
    setForgotPasswordEmail(email);
    setForgotPasswordMessage('');
    setForgotPasswordError('');
    setValidationError(''); // Clear validation error when switching views
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordMessage('');
    setForgotPasswordError('');
    setValidationError(''); // Clear validation error when switching back
    if (forgotPasswordEmail && forgotPasswordEmail !== email) {
      setEmail(forgotPasswordEmail);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (isResettingPassword) return;
    
    setForgotPasswordError('');
    setForgotPasswordMessage('');

    if (!forgotPasswordEmail) {
      setForgotPasswordError(t('auth.forgotPassword.enterEmail'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      setForgotPasswordError(t('auth.forgotPassword.validEmail'));
      return;
    }

    setIsResettingPassword(true);

    try {
      const response = await authAPI.forgotPassword(forgotPasswordEmail, 'user');
      
      if (response.success) {
        setForgotPasswordMessage(t('auth.forgotPassword.successMessage'));
        setForgotPasswordEmail('');
        setTimeout(() => {
          handleBackToLogin();
        }, 5000);
      } else {
        setForgotPasswordError(response.message || t('auth.forgotPassword.errorSending'));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotPasswordError(
        err.data?.message || 
        err.message || 
        t('auth.forgotPassword.errorOccurred')
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-sky-100 px-4 py-8 selection:bg-cyan-500 selection:text-white">
          <div className="w-full max-w-md bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-slate-200/80">
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                <EnvelopeIcon className="h-8 w-8 text-cyan-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">{t('auth.forgotPassword.title')}</h1>
              <p className="text-slate-600">
                {t('auth.forgotPassword.subtitle')}
              </p>
            </div>

            {forgotPasswordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-700">{forgotPasswordError}</span>
              </div>
            )}

            {forgotPasswordMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-green-700">{forgotPasswordMessage}</span>
              </div>
            )}

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('auth.forgotPassword.emailLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    ref={forgotEmailInputRef}
                    id="forgot-email"
                    name="forgot-email"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="block w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition duration-200 placeholder-slate-400 disabled:opacity-50"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    required
                    disabled={isResettingPassword}
                    autoComplete="email"
                  />
                </div>
              </div>

              <button
                type="submit"
                className={`w-full py-3.5 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center ${
                  isResettingPassword 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-400 cursor-not-allowed opacity-90' 
                    : 'bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600'
                }`}
                disabled={isResettingPassword}
              >
                {isResettingPassword ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="animate-pulse">{t('auth.forgotPassword.sending')}</span>
                  </>
                ) : (
                  t('auth.forgotPassword.sendInstructions')
                )}
              </button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full py-3 text-slate-700 font-medium rounded-xl border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center"
                disabled={isResettingPassword}
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                {t('auth.forgotPassword.backToSignIn')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                {t('auth.forgotPassword.rememberPassword')}{' '}
                <button 
                  onClick={handleBackToLogin}
                  className="font-medium text-cyan-600 hover:text-cyan-500"
                  disabled={isResettingPassword}
                >
                  {t('auth.forgotPassword.signInLink')}
                </button>
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Regular Login View
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-sky-100 px-4 py-8 selection:bg-cyan-500 selection:text-white">
        <div className="w-full max-w-md bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-slate-200/80">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-4">{institutionName || 'Institution'}</h1>
          </div>

          {/* DISPLAY BOTH VALIDATION ERRORS AND LOGIN ERRORS */}
          {(validationError || loginError) && (
            <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-400 text-red-700 rounded-lg text-sm text-center" role="alert">
              {validationError || getErrorMessage(loginError)}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleFormSubmit} noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('auth.institutionLogin.emailLabel')}
              </label>
              <input
                ref={emailInputRef}
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                className="block w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition duration-200 placeholder-slate-400 disabled:opacity-50"
                placeholder={t('auth.institutionLogin.emailPlaceholder')}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('auth.institutionLogin.passwordLabel')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                className="block w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition duration-200 placeholder-slate-400 disabled:opacity-50"
                placeholder={t('auth.institutionLogin.passwordPlaceholder')}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                <input 
                  id="remember-me" 
                  name="remember-me" 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-cyan-600 border-slate-300 rounded focus:ring-cyan-500" 
                  disabled={isLoading}
                />
                <label htmlFor="remember-me" className="ml-2 block text-slate-700">
                  {t('auth.institutionLogin.rememberMe')}
                </label>
              </div>
              <button 
                type="button"
                onClick={handleForgotPasswordClick}
                className="font-medium text-cyan-600 hover:text-cyan-500"
                disabled={isLoading}
              >
                {t('auth.institutionLogin.forgotPassword')}
              </button>
            </div>

            <button
              type="submit"
              className={`w-full py-3.5 mt-6 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center ${
                isLoading 
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-400 cursor-not-allowed opacity-90' 
                  : 'bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 disabled:opacity-70'
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="animate-pulse">{t('auth.institutionLogin.signingIn')}</span>
                </>
              ) : (
                t('auth.institutionLogin.signIn')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              {t('auth.institutionLogin.newUser')}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default InstitutionLogin;